import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import VideoPlayer from './VideoPlayer';
import NavigationControlsNew from './NavigationControlsNew';
import NavigationControls from './NavigationControls';
/**
 * Simplified video session component for comparison view
 * Combines video player and navigation controls without telemetry
 * Designed to work with external sync management
 */
function ComparisonVideoSession({
  videoId,
  sessionData,
  sessionMetadata = null,
  isLoading = false,
  error = null,
  label = '',
  // Sync props
  currentTime = 0,
  isPlaying = false,
  playbackRate = 1,
  selectedLap = 1,
  videoMode = 'sync', // 'sync' | 'individual'
  // Event handlers
  onLapSelection,
  // Individual control handlers (for nudging)
  onIndividualPlay,
  onIndividualPause,
  onIndividualSeek,
  className = '',
  debugMode = false,
}) {
  const [skipSeconds, setSkipSeconds] = useState(1);
  const [individualPlayState, setIndividualPlayState] = useState(null); // Individual play state when in individual mode
  const [individualCurrentTime, setIndividualCurrentTime] = useState(null); // Individual time when in individual mode

  // Calculate current lap and position
  const currentPosition = useMemo(() => {
    if (!sessionData?.lapData || sessionData.lapData.length === 0) {
      return { lap: selectedLap || 1, segment: 1 };
    }
    // console.log(sessionData);
    // Find current lap based on video time
    let currentLap = selectedLap || 1;

    // Find the correct lap by checking start times
    for (let i = 0; i < sessionData.lapData.length; i++) {
      const lap = sessionData.lapData[i];
      const nextLap = sessionData.lapData[i + 1];

      if (currentTime >= lap.startTime) {
        // If there's no next lap, or current time is before next lap starts
        if (!nextLap || currentTime < nextLap.startTime) {
          currentLap = lap.number;
          break;
        }
      }
    }

    return { lap: currentLap, segment: 1 };
  }, [currentTime, sessionData?.lapData, selectedLap]);

  // Handle navigation events
  const handleNavigateToLap = useCallback(
    (lapNumber) => {
      // Reset individual state when changing laps
      setIndividualPlayState(null);
      setIndividualCurrentTime(null);
      onLapSelection?.(videoId, lapNumber);
    },
    [videoId, onLapSelection]
  );

  const handleSkipSeconds = useCallback(
    (seconds) => {
      const newTime = Math.max(0, currentTime + seconds);
      // This is an individual control action, so use individual handler
      onIndividualSeek?.(videoId, newTime);

      // ALSO update local state if in individual mode
      if (videoMode === 'individual') {
        setIndividualCurrentTime(newTime);
      }
    },
    [videoId, currentTime, onIndividualSeek, videoMode]
  );

  const handleSkipLap = useCallback(
    (direction) => {
      const currentIndex = sessionData?.lapData?.findIndex(
        (lap) => lap.number === selectedLap
      );
      if (currentIndex !== undefined && currentIndex !== -1) {
        const newIndex = currentIndex + direction;
        if (newIndex >= 0 && newIndex < sessionData.lapData.length) {
          const newLap = sessionData.lapData[newIndex];
          handleNavigateToLap(newLap.number);
        }
      }
    },
    [sessionData?.lapData, selectedLap, handleNavigateToLap]
  );

  // Handle individual video controls
  const handleIndividualPlay = useCallback(() => {
    setIndividualPlayState(true);
    // Initialize individual time to current sync time when starting individual control
    if (individualCurrentTime === null) {
      setIndividualCurrentTime(currentTime);
    }

    // Update sync system with current position when switching to individual mode
    const currentVideoTime =
      individualCurrentTime !== null ? individualCurrentTime : currentTime;
    onIndividualSeek?.(videoId, currentVideoTime);

    onIndividualPlay?.(videoId);
  }, [
    videoId,
    onIndividualPlay,
    individualCurrentTime,
    currentTime,
    onIndividualSeek,
  ]);

  const handleIndividualPause = useCallback(() => {
    setIndividualPlayState(false);

    // Update sync system with current position when switching to individual mode
    const currentVideoTime =
      individualCurrentTime !== null ? individualCurrentTime : currentTime;
    onIndividualSeek?.(videoId, currentVideoTime);

    onIndividualPause?.(videoId);
  }, [
    videoId,
    onIndividualPause,
    individualCurrentTime,
    currentTime,
    onIndividualSeek,
  ]);

  const handleIndividualSeek = useCallback(
    (newTime) => {
      onIndividualSeek?.(videoId, newTime);
    },
    [videoId, onIndividualSeek]
  );

  // Handle video player events (no longer sends updates to sync system)
  const handleVideoTimeUpdate = useCallback(
    (time) => {
      if (videoMode === 'individual') {
        // In individual mode, track time locally
        setIndividualCurrentTime(time);
      }
      // In sync mode, videos now follow the timeline instead of driving it
    },
    [videoMode]
  );

  // Loading state
  if (isLoading) {
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          flexDirection: 'column',
          gap: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading {label || 'video'}...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        className={className}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: 1,
        }}
      >
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Failed to load {label || 'video'}
          </Typography>
          <Typography variant="body2">
            {error.message || 'An unknown error occurred'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // No session data
  if (!sessionData) {
    return (
      <Box
        className={className}
        sx={{
          p: 2,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No {label || 'video'} data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Header with label */}
      {label && (
        <Box
          sx={{
            p: 1,
            backgroundColor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {sessionData.metadata?.drivername}
            {sessionData.metadata?.carNum &&
              ` - Car #${sessionData.metadata.carNum}`}
            {/* {sessionMetadata?.trackConfig &&
              ` • ${sessionMetadata.trackConfig}`}
            {sessionMetadata?.eventVenue && ` • ${sessionMetadata.eventVenue}`} */}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sessionMetadata?.eventName && `${sessionMetadata.eventName}`}
            {sessionMetadata?.eventDate &&
              ` • ${new Date(sessionMetadata.eventDate).toLocaleString()}`}
            {sessionData.metadata?.createdAt &&
              ` • Created: ${new Date(
                sessionData.metadata.createdAt
              ).toLocaleString()}`}
          </Typography>

          {/* Debug Info */}
          {debugMode && (
            <Box
              sx={{
                mt: 0.5,
                p: 0.5,
                backgroundColor: 'grey.200',
                borderRadius: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: '0.65rem', fontFamily: 'monospace' }}
              >
                CrossStart: {sessionData.crossStart?.toFixed(1)}s | Current:{' '}
                {currentTime?.toFixed(1)}s | Selected Lap: {selectedLap} | Total
                Laps: {sessionData.lapData?.length || 0}
              </Typography>
              {sessionData.lapData?.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6rem',
                    fontFamily: 'monospace',
                    display: 'block',
                    mt: 0.2,
                  }}
                >
                  Lap {selectedLap} starts at:{' '}
                  {sessionData.lapData
                    ?.find((l) => l.number === selectedLap)
                    ?.startTime?.toFixed(1) || 'N/A'}
                  s
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Video Player */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <VideoPlayer
          videoUrl={sessionData.videoUrl}
          currentTime={
            videoMode === 'individual' && individualCurrentTime !== null
              ? individualCurrentTime
              : currentTime
          }
          isPlaying={
            videoMode === 'individual' && individualPlayState !== null
              ? individualPlayState
              : isPlaying
          }
          playbackRate={playbackRate}
          onTimeUpdate={handleVideoTimeUpdate}
          showControls={false}
        />
      </Box>

      {/* Navigation Controls */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      >
        <NavigationControlsNew
          lapData={sessionData.lapData || []}
          currentLap={selectedLap}
          skipSeconds={skipSeconds}
          isPlaying={
            videoMode === 'individual' && individualPlayState !== null
              ? individualPlayState
              : isPlaying
          }
          onNavigateToLap={handleNavigateToLap}
          onSkipSeconds={handleSkipSeconds}
          onSkipLap={handleSkipLap}
          onSetSkipSeconds={setSkipSeconds}
          onPlay={handleIndividualPlay}
          onPause={handleIndividualPause}
        />
        {/* <NavigationControls
          lapData={sessionData.lapData || []}
          currentLap={selectedLap}
          skipSeconds={skipSeconds}
          // isPlaying={videoMode === 'individual' && individualPlayState !== null ? individualPlayState : isPlaying}
          onNavigateToLap={handleNavigateToLap}
          onSkipSeconds={handleSkipSeconds}
          onSkipLap={handleSkipLap}
          onSetSkipSeconds={setSkipSeconds}
          // onPlay={handleIndividualPlay}
          // onPause={handleIndividualPause}
          showLapControls={true}
          showSegmentControls={false}
          showSkipControls={true}
          compact={false}
        /> */}
      </Box>
      {/* <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: 1,
          justifyContent: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 1,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: 'center' }}
        >
          Individual:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <button
            onClick={handleIndividualPlay}
            style={{
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: '0.7rem',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            Play
          </button>
          <button
            onClick={handleIndividualPause}
            style={{
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: '0.7rem',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            Pause
          </button>
        </Box> 
      </Box>*/}
    </Box>
  );
}

export default ComparisonVideoSession;

// showLapControls={true}
// showSegmentControls={false}
// showSkipControls={true}
// compact={true}
// />

// {/* Individual Control Buttons */}
{
  /* <Box sx={{ 
display: 'flex', 
gap: 1, 
mt: 1, 
justifyContent: 'center',
borderTop: '1px solid',
borderColor: 'divider',
pt: 1
}}>
<Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
  Individual:
</Typography>
<Box sx={{ display: 'flex', gap: 0.5 }}>
  <button
    onClick={handleIndividualPlay}
    style={{
      border: '1px solid #ccc',
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: '0.7rem',
      backgroundColor: 'white',
      cursor: 'pointer'
    }}
  >
    Play
  </button>
  <button
    onClick={handleIndividualPause}
    style={{
      border: '1px solid #ccc',
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: '0.7rem',
      backgroundColor: 'white',
      cursor: 'pointer'
    }}
  >
    Pause
  </button>
</Box> */
}
